import { useEffect, useMemo, useState, useCallback } from "react";
import api, { inr } from "@/lib/api";
import { DualScroll } from "@/components/events/DualScroll";
import { RandomTagDialog } from "@/components/events/RandomTagDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Check, RotateCcw, Trash2, MessageCircle, FileDown, FileSpreadsheet, Search, Image as ImageIcon, Copy, IdCard, Plus, Tags, KeyRound, Tag, Shuffle, Crop } from "lucide-react";
import PhotoCropDialog from "@/components/idc/PhotoCropDialog";
import { toast } from "sonner";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import JSZip from "jszip";
import { eventShareUrl, passShareUrl, passQrPayload } from "@/lib/eventLib";
import { renderPassCanvases, exportWidthPx } from "@/lib/passRender";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export default function SubmissionsView({ event, onBack }) {
  const [subs, setSubs] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("all");
  const [search, setSearch] = useState("");
  const [cols, setCols] = useState(() => Object.fromEntries((event.fields || []).map((f) => [f.key, true])));
  const [viewImg, setViewImg] = useState("");
  const [adjust, setAdjust] = useState(null);

  const onAdjustDone = async (durl) => {
    const { sid, key } = adjust;
    setAdjust(null);
    try {
      const r = await api.post(`/events/submissions/${sid}/photo`, { key, data: durl });
      setPhotos((p) => ({ ...p, [r.data.id]: durl }));
      setSubs((list) => list.map((x) => (x.id === sid ? { ...x, data: { ...x.data, [key]: r.data.id } } : x)));
      toast.success("Photo adjust ho gayi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo save nahi hui"); }
  };
  const [zipBusy, setZipBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [rules, setRules] = useState(() => {
    if (event.serial_prefix_rules?.length) return event.serial_prefix_rules;
    if (event.serial_prefix) return [{ from: 1, to: 999999, prefix: event.serial_prefix }];
    return [];
  });
  const [rulesOpen, setRulesOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [rangeList, setRangeList] = useState([]);
  const [tagFor, setTagFor] = useState(null);
  const [tagText, setTagText] = useState("");
  const [rtagOpen, setRtagOpen] = useState(false);

  const saveTag = async () => {
    try {
      await api.post(`/events/submissions/${tagFor.id}/tag`, { tag: tagText });
      toast.success(tagText.trim() ? "Tag lag gaya" : "Tag hata diya");
      setTagFor(null);
      load();
    } catch { toast.error("Tag save nahi hua"); }
  };

  useEffect(() => {
    if (event.serial_prefix_rules?.length) setRules(event.serial_prefix_rules);
    else if (event.serial_prefix) setRules([{ from: 1, to: 999999, prefix: event.serial_prefix }]);
  }, [event.serial_prefix_rules, event.serial_prefix]);

  const prefixFor = (s) => {
    if (!s?.serial_no) return "";
    // Pehle field-value based rules (jaise Age 12-18 -> Junior)
    for (const r of rules) {
      if (!r.field) continue;
      const raw = s.data?.[r.field];
      if (raw === undefined || raw === "") continue;
      const v = Number(raw);
      if (!Number.isNaN(v) && v >= (Number(r.from) || 0) && v <= (Number(r.to) || 0)) return r.prefix || "";
    }
    const n = s.serial_no;
    const r = rules.find((x) => !x.field && n >= (Number(x.from) || 0) && n <= (Number(x.to) || 0));
    return r ? (r.prefix || "") : "";
  };
  const serialLabel = (s) => (s?.serial_no ? `${prefixFor(s)}${String(s.serial_no).padStart(3, "0")}` : "");

  const openRules = () => { setDraft(rules.length ? rules.map((r) => ({ ...r })) : [{ from: 1, to: 999999, prefix: "" }]); setRulesOpen(true); };
  const saveRules = async () => {
    const clean = draft.map((r) => ({ from: Number(r.from) || 0, to: Number(r.to) || 0, prefix: (r.prefix || "").trim(), field: r.field || "" })).filter((r) => Number(r.to) > 0);
    try {
      await api.post(`/events/${event.id}/serial-prefix-rules`, { rules: clean });
      setRules(clean); setRulesOpen(false);
      toast.success("Prefix rules save ho gaye");
    } catch { toast.error("Save nahi hua"); }
  };

  const openPdfRange = (list) => {
    const appr = list.filter((s) => s.status === "approved" && s.pass_token && s.serial_no);
    if (!appr.length) return toast.error("Koi approved pass (serial ke saath) select nahi hai");
    const serials = appr.map((s) => s.serial_no);
    setRangeList(appr);
    setRangeFrom(String(Math.min(...serials)));
    setRangeTo(String(Math.max(...serials)));
    setRangeOpen(true);
  };
  const confirmPdfRange = () => {
    const f = Number(rangeFrom) || 0, t = Number(rangeTo) || 0;
    const subset = rangeList.filter((s) => s.serial_no >= f && s.serial_no <= t);
    if (!subset.length) return toast.error("Is range me koi pass nahi hai");
    setRangeOpen(false);
    downloadPassesPDF(subset);
  };

  const tmpl = event.pass_template || {};
  const hasTmpl = !!(tmpl.enabled && tmpl.image && (tmpl.elements || []).length);

  const fields = event.fields || [];
  const catName = (id) => (event.payment_categories || []).find((c) => c.id === id)?.name || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${event.id}/submissions`);
      setSubs(r.data);
      const ids = [];
      r.data.forEach((s) => {
        fields.filter((f) => f.type === "photo").forEach((f) => { if (s.data?.[f.key]) ids.push(s.data[f.key]); });
        if (s.payment_screenshot) ids.push(s.payment_screenshot);
      });
      if (ids.length) {
        const pr = await api.post("/events/photos/batch", { ids });
        setPhotos(pr.data);
      }
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = subs;
    if (statusF !== "all") list = list.filter((s) => s.status === statusF);
    const q = search.trim().toLowerCase();
    if (q) {
      const num = /^\d+$/.test(q) ? Number(q) : null;
      list = list.filter((s) => (num !== null && s.serial_no === num)
        || (s.serial_no && serialLabel(s).toLowerCase().includes(q))
        || Object.values(s.data || {}).some((v) => String(v).toLowerCase().includes(q)));
    }
    return list;
  }, [subs, statusF, search, rules]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (s, approve) => {
    await api.post(`/events/submissions/${s.id}/${approve ? "approve" : "unapprove"}`);
    toast.success(approve ? "Approved" : "Wapas pending");
    load();
  };
  const del = async (s) => { if (!window.confirm("Delete this submission?")) return; await api.delete(`/events/submissions/${s.id}`); load(); };

  const selList = filtered.filter((s) => sel[s.id]);
  const selCount = selList.length;
  const pendingSel = selList.filter((s) => s.status !== "approved");
  const approvedSel = selList.filter((s) => s.status === "approved" && s.pass_token);
  const allSel = filtered.length > 0 && filtered.every((s) => sel[s.id]);
  const toggleAll = () => { if (allSel) setSel({}); else setSel(Object.fromEntries(filtered.map((s) => [s.id, true]))); };

  const bulkApprove = async () => {
    const ids = pendingSel.map((s) => s.id);
    if (!ids.length) return toast.error("Koi pending selected nahi hai");
    const r = await api.post(`/events/${event.id}/submissions/bulk-approve`, { ids });
    toast.success(`${r.data.approved} submissions approve ho gayi`);
    setSel({});
    load();
  };

  const downloadPassesPDF = async (list) => {
    const appr = list.filter((s) => s.status === "approved" && s.pass_token);
    if (!appr.length) return toast.error("Koi approved pass select nahi hai");
    setPdfBusy(true);
    toast.info(`${appr.length} pass ka 12x18 PDF ban raha hai...`);
    try {
      const pageW = 12, pageH = 18, margin = 0, gap = 0;
      const unitPerInch = tmpl.unit === "mm" ? 25.4 : 2.54;
      const pw = (Number(tmpl.width) || 10) / unitPerInch;
      const ph = (Number(tmpl.height) || 6) / unitPerInch;
      const cols = Math.max(1, Math.floor((pageW - 2 * margin + gap) / (pw + gap)));
      const rows = Math.max(1, Math.floor((pageH - 2 * margin + gap) / (ph + gap)));
      const perPage = cols * rows;
      const exW = exportWidthPx(tmpl);
      const items = [];
      for (let i = 0; i < appr.length; i++) {
        const s = appr[i];
        const data = { ...(s.data || {}) };
        fields.filter((f) => f.type === "photo").forEach((f) => { if (data[f.key] && photos[data[f.key]]) data[f.key] = photos[data[f.key]]; });
        const nameField = fields.find((f) => f.type === "text" && s.data?.[f.key]);
        const cand = nameField ? s.data[nameField.key] : "Candidate";
        const qrDataUrl = await QRCode.toDataURL(passQrPayload(s.pass_token), { width: 600, margin: 1 });
        items.push({ ctx: { data, serial_no: s.serial_no, candidate_name: cand, event_name: event.name, serial_prefix: prefixFor(s), tag: s.tag || "", qrDataUrl } });
      }
      const canvases = await renderPassCanvases({ template: tmpl, items, widthPx: exW });
      const doc = new jsPDF({ unit: "in", format: [pageW, pageH], orientation: "portrait" });
      for (let i = 0; i < canvases.length; i++) {
        const idxOnPage = i % perPage;
        if (i > 0 && idxOnPage === 0) doc.addPage([pageW, pageH], "portrait");
        const col = idxOnPage % cols;
        const row = Math.floor(idxOnPage / cols);
        const x = margin + col * (pw + gap);
        const y = margin + row * (ph + gap);
        doc.addImage(canvases[i].toDataURL("image/jpeg", 0.92), "JPEG", x, y, pw, ph);
      }
      doc.save(`${event.name}-passes-12x18.pdf`);
      toast.success(`${appr.length} pass 12x18 PDF me download ho gaye`);
    } catch (e) {
      toast.error("PDF banane me dikkat aayi");
    } finally { setPdfBusy(false); }
  };

  const buildMsg = (s) => {
    const link = s.pass_token ? passShareUrl(s.pass_token, event.updated_at) : eventShareUrl(event.share_token, event.updated_at);
    const nameField = fields.find((f) => f.type === "text" && s.data?.[f.key]);
    const cand = nameField ? s.data[nameField.key] : "Candidate";
    return `Namaste ${cand}! Aapka form "${event.name}" ke liye APPROVED ho gaya hai. \u2705\nAapka Entry Pass: ${link}\n${event.contact_number ? `Query: ${event.contact_number}` : ""}`;
  };

  const whatsapp = (s) => {
    const msg = buildMsg(s);
    navigator.clipboard.writeText(msg).catch(() => {});
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("WhatsApp khul raha hai — message copy bhi ho gaya");
  };

  const copyPassLink = async (s) => {
    const text = buildMsg(s);
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; }
    catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) toast.success("Approval message copy ho gaya — kahin bhi paste karein");
    else toast.error("Copy nahi hua");
  };

  const activeFields = fields.filter((f) => cols[f.key]);

  const downloadPasses = async () => {
    const appr = filtered.filter((s) => s.status === "approved" && s.pass_token);
    if (!appr.length) return toast.error("Koi approved pass nahi mila");
    setZipBusy(true);
    toast.info(`${appr.length} pass ban rahe hain...`);
    try {
      const exW = exportWidthPx(tmpl);
      const items = [];
      for (let i = 0; i < appr.length; i++) {
        const s = appr[i];
        const data = { ...(s.data || {}) };
        fields.filter((f) => f.type === "photo").forEach((f) => { if (data[f.key] && photos[data[f.key]]) data[f.key] = photos[data[f.key]]; });
        const nameField = fields.find((f) => f.type === "text" && s.data?.[f.key]);
        const cand = nameField ? s.data[nameField.key] : "Candidate";
        const qrDataUrl = await QRCode.toDataURL(passQrPayload(s.pass_token), { width: 600, margin: 1 });
        items.push({ ctx: { data, serial_no: s.serial_no, candidate_name: cand, event_name: event.name, serial_prefix: prefixFor(s), tag: s.tag || "", qrDataUrl }, fname: `${String(s.serial_no || i + 1).padStart(3, "0")}-${String(cand).replace(/[^a-z0-9]+/gi, "-")}.jpg` });
      }
      const canvases = await renderPassCanvases({ template: tmpl, items, widthPx: exW });
      const zip = new JSZip();
      for (let i = 0; i < canvases.length; i++) {
        const blob = await new Promise((res) => canvases[i].toBlob(res, "image/jpeg", 0.92));
        zip.file(items[i].fname, blob);
      }
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a"); a.href = url; a.download = `${event.name}-passes.zip`; a.click(); URL.revokeObjectURL(url);
      toast.success(`${appr.length} pass ZIP me download ho gaye`);
    } catch (e) {
      toast.error("Pass download fail hua");
    } finally { setZipBusy(false); }
  };

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Submissions");
    const header = ["#", "Serial", ...activeFields.map((f) => f.label), ...(event.payment_required ? ["Category", "Amount", "Ref"] : []), "Status", "Date"];
    ws.addRow(header);
    ws.getRow(1).font = { bold: true };
    filtered.forEach((s, i) => {
      const row = ["", serialLabel(s), ...activeFields.map((f) => (f.type === "photo" ? "" : (s.data?.[f.key] || ""))), ...(event.payment_required ? [catName(s.payment_category_id), s.payment_amount || "", s.payment_ref || ""] : []), s.status, (s.created_at || "").slice(0, 10)];
      row[0] = i + 1;
      const added = ws.addRow(row);
      let hasImg = false;
      activeFields.forEach((f, idx) => {
        if (f.type === "photo") {
          const durl = photos[s.data?.[f.key]];
          if (durl) { hasImg = true; const id = wb.addImage({ base64: durl, extension: "jpeg" }); ws.addImage(id, { tl: { col: 2 + idx, row: added.number - 1 }, ext: { width: 46, height: 46 } }); }
        }
      });
      if (hasImg) added.height = 38;
    });
    ws.columns.forEach((c) => { c.width = 16; });
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf]));
    const a = document.createElement("a"); a.href = url; a.download = `${event.name}-submissions.xlsx`; a.click(); URL.revokeObjectURL(url);
    toast.success("Excel downloaded");
  };

  const exportPDF = async () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210, M = 12; let y = M;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.text(event.name, M, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120);
    doc.text(`Submissions: ${filtered.length} · Checked-in: ${filtered.filter((x) => x.checked_in).length}`, M, y); doc.setTextColor(0); y += 5;
    for (let i = 0; i < filtered.length; i++) {
      const s = filtered[i];
      const nameField = activeFields.find((f) => f.type === "text" && s.data?.[f.key]);
      const cand = nameField ? s.data[nameField.key] : "";
      const photoFields = activeFields.filter((f) => f.type === "photo" && photos[s.data?.[f.key]]);
      const need = 15 + activeFields.length * 5 + (photoFields.length ? 28 : 0) + (event.payment_required ? 6 : 0);
      if (y + need > 285) { doc.addPage(); y = M; }
      doc.setDrawColor(220); doc.line(M, y, W - M, y); y += 5;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text(`#${i + 1}  ${s.serial_no ? (prefixFor(s) || "Sr. ") + String(s.serial_no).padStart(3, "0") + "  " : ""}${cand}`, M, y);
      doc.setTextColor(s.status === "approved" ? 22 : 200, s.status === "approved" ? 150 : 120, 60);
      doc.text(s.status.toUpperCase(), W - M, y, { align: "right" }); doc.setTextColor(0); y += 5;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      activeFields.filter((f) => f.type !== "photo").forEach((f) => { doc.text(`${f.label}: ${s.data?.[f.key] || "-"}`, M, y); y += 5; });
      if (event.payment_required) { doc.text(`Payment: ${catName(s.payment_category_id)} ${s.payment_amount ? inr(s.payment_amount) : ""} ${s.payment_ref ? "Ref:" + s.payment_ref : ""}`, M, y); y += 5; }
      doc.setTextColor(s.checked_in ? 22 : 150, s.checked_in ? 150 : 150, s.checked_in ? 60 : 150);
      doc.text(`Check-in: ${s.checked_in ? "YES  " + String(s.checked_at || "").replace("T", " ").slice(0, 16) : "No"}`, M, y);
      doc.setTextColor(0); y += 5;
      if (photoFields.length) {
        let x = M;
        photoFields.forEach((f) => { try { doc.addImage(photos[s.data[f.key]], "JPEG", x, y, 24, 24); } catch { /* skip */ } x += 27; });
        y += 27;
      }
      y += 2;
    }
    doc.save(`${event.name}-submissions.pdf`);
    toast.success("PDF downloaded");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="submissions-view">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex"><div className="flex-1 bg-cyan-500" /><div className="flex-1 bg-pink-600" /><div className="flex-1 bg-yellow-400" /><div className="flex-1 bg-slate-900" /></div>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={onBack} className="gap-1 -ml-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
          <p className="font-heading font-bold flex-1 truncate">{event.name} — Submissions</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-40" data-testid="subs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search value / Serial No." className="pl-8" data-testid="subs-search" />
          </div>
          <Button variant="outline" onClick={openRules} data-testid="subs-prefix-btn" className="gap-1.5 h-9"><Tags className="h-4 w-4" /> Prefix{rules.length ? ` (${rules.length})` : ""}</Button>
          <Button variant="outline" onClick={() => setRtagOpen(true)} data-testid="subs-random-tag-btn" className="gap-1.5 h-9 text-amber-700"><Shuffle className="h-4 w-4" /> Random Tag</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={exportExcel} data-testid="subs-excel-btn" className="gap-1.5"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
          <Button variant="outline" onClick={exportPDF} data-testid="subs-pdf-btn" className="gap-1.5"><FileDown className="h-4 w-4" /> PDF</Button>
          {hasTmpl && <Button onClick={downloadPasses} disabled={zipBusy} data-testid="subs-passes-btn" className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white"><IdCard className="h-4 w-4" /> {zipBusy ? "Ban rahe..." : "Passes (ZIP)"}</Button>}
        </div>

        {fields.length > 0 && (
          <div className="flex flex-wrap gap-3 text-xs bg-slate-50 border border-border rounded-lg px-3 py-2">
            <span className="text-muted-foreground font-medium">Export columns:</span>
            {fields.map((f) => (
              <label key={f.key} className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={!!cols[f.key]} onChange={(e) => setCols({ ...cols, [f.key]: e.target.checked })} data-testid={`subs-col-${f.key}`} /> {f.label}</label>
            ))}
          </div>
        )}

        <p className="text-sm text-muted-foreground" data-testid="subs-count">
          {filtered.length} submission{filtered.length === 1 ? "" : "s"}
          <span className="text-emerald-600 font-semibold"> · {subs.filter((x) => x.checked_in).length} checked-in</span>
        </p>

        {selCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-pink-50 border border-pink-200 rounded-lg px-3 py-2" data-testid="subs-bulk-bar">
            <span className="text-sm font-semibold text-pink-700" data-testid="subs-sel-count">{selCount} selected</span>
            <div className="flex-1" />
            <Button size="sm" onClick={bulkApprove} disabled={!pendingSel.length} data-testid="subs-bulk-approve-btn" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4" /> Approve ({pendingSel.length})</Button>
            {hasTmpl && <Button size="sm" onClick={() => openPdfRange(selList)} disabled={pdfBusy || !approvedSel.length} data-testid="subs-bulk-pdf-btn" className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white"><FileDown className="h-4 w-4" /> {pdfBusy ? "Ban raha..." : `PDF 12x18 (${approvedSel.length})`}</Button>}
            <Button size="sm" variant="ghost" onClick={() => setSel({})} data-testid="subs-bulk-clear-btn">Clear</Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="subs-empty"><p className="text-sm text-muted-foreground">Koi submission nahi mili.</p></div>
        ) : (
          <DualScroll className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="subs-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-2.5"><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="subs-check-all" /></th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">#</th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Serial</th>
                  {activeFields.map((f) => <th key={f.key} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</th>)}
                  {event.payment_required && <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</th>}
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id} className="border-t border-border hover:bg-slate-50/60 align-top" data-testid={`sub-row-${i}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={!!sel[s.id]} onChange={() => setSel((m) => ({ ...m, [s.id]: !m[s.id] }))} data-testid={`sub-check-${i}`} /></td>
                    <td className="px-3 py-3 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-3 font-bold text-cyan-700" data-testid={`sub-serial-${i}`}>{s.serial_no ? serialLabel(s) : "—"}</td>
                    {activeFields.map((f) => (
                      <td key={f.key} className="px-3 py-3">
                        {f.type === "photo" ? (
                          photos[s.data?.[f.key]] ? (
                            <div className="flex items-center gap-1.5">
                              <img src={photos[s.data[f.key]]} alt="" className="h-10 w-10 rounded object-cover border cursor-pointer" onClick={() => setViewImg(photos[s.data[f.key]])} />
                              <button type="button" title="Adjust photo" onClick={() => setAdjust({ sid: s.id, key: f.key, src: photos[s.data[f.key]] })} className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100" data-testid={`sub-photo-adjust-${i}-${f.key}`}><Crop className="h-3.5 w-3.5" /></button>
                            </div>
                          ) : <span className="text-slate-300">—</span>
                        ) : (s.data?.[f.key] || <span className="text-slate-300">—</span>)}
                      </td>
                    ))}
                    {event.payment_required && (
                      <td className="px-3 py-3 text-xs">
                        {catName(s.payment_category_id) && <div>{catName(s.payment_category_id)}</div>}
                        {s.payment_amount ? <div className="font-semibold">{inr(s.payment_amount)}</div> : null}
                        {s.payment_ref && <div className="text-muted-foreground">Ref: {s.payment_ref}</div>}
                        {s.payment_screenshot && photos[s.payment_screenshot] && <img src={photos[s.payment_screenshot]} alt="" className="h-9 w-9 rounded object-cover border cursor-pointer mt-1" onClick={() => setViewImg(photos[s.payment_screenshot])} data-testid={`sub-payimg-${i}`} />}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${s.status === "approved" ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>{s.status}</span>
                      {s.source === "master" && <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full text-pink-700 bg-pink-50" data-testid={`sub-master-${i}`}>Master</span>}
                      {s.pass_key && <span className="ml-1 inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full text-indigo-700 bg-indigo-50" data-testid={`sub-key-${i}`} title="Candidate pass key"><KeyRound className="h-3 w-3" /> {s.pass_key}</span>}
                      {s.tag && <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full text-amber-800 bg-amber-100 border border-amber-300" data-testid={`sub-tag-${i}`}>{s.tag}</span>}
                      {s.checked_in && <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full text-white bg-emerald-600" data-testid={`sub-checkedin-${i}`}>In</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {s.status === "pending" ? (
                          <Button size="sm" variant="ghost" onClick={() => setStatus(s, true)} data-testid={`sub-approve-${i}`} className="h-7 text-emerald-600 hover:bg-emerald-50" title="Approve"><Check className="h-4 w-4" /></Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setStatus(s, false)} data-testid={`sub-unapprove-${i}`} className="h-7 text-slate-500 hover:bg-slate-100" title="Unapprove"><RotateCcw className="h-4 w-4" /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => whatsapp(s)} data-testid={`sub-whatsapp-${i}`} className="h-7 text-green-600 hover:bg-green-50" title="WhatsApp"><MessageCircle className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => copyPassLink(s)} data-testid={`sub-copylink-${i}`} className="h-7 text-cyan-700 hover:bg-cyan-50" title="Pass Link Copy"><Copy className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setTagFor(s); setTagText(s.tag || ""); }} data-testid={`sub-tag-btn-${i}`} className="h-7 text-amber-600 hover:bg-amber-50" title="Pass par tag lagayein"><Tag className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del(s)} data-testid={`sub-delete-${i}`} className="h-7 text-red-500 hover:bg-red-50" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DualScroll>
        )}
      </main>

      {viewImg && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setViewImg("")} data-testid="sub-img-modal">
          <img src={viewImg} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg" />
        </div>
      )}

      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-lg" data-testid="prefix-rules-dialog">
          <DialogHeader><DialogTitle>Serial Prefix Rules</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Serial range YA kisi number field (jaise Age) ki value ke hisaab se prefix. Jaise Serial 1–50 → "RK-", ya Age 12–18 → "Junior-", Age 18–60 → "Senior-".</p>
          <div className="space-y-2 max-h-[46vh] overflow-y-auto">
            <div className="grid grid-cols-[1.3fr_0.8fr_0.8fr_1fr_auto] gap-2 text-[11px] font-semibold text-muted-foreground px-1">
              <span>Basis</span><span>From</span><span>To</span><span>Prefix</span><span />
            </div>
            {draft.map((r, idx) => (
              <div key={idx} className="grid grid-cols-[1.3fr_0.8fr_0.8fr_1fr_auto] gap-2 items-center" data-testid={`prefix-rule-${idx}`}>
                <select value={r.field || ""} onChange={(e) => setDraft(draft.map((x, i) => (i === idx ? { ...x, field: e.target.value } : x)))} className="h-9 text-xs border border-input rounded-md px-1.5 bg-white" data-testid={`prefix-field-${idx}`}>
                  <option value="">Serial No.</option>
                  {fields.filter((f) => f.type !== "photo").map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <Input type="number" min={0} value={r.from} onChange={(e) => setDraft(draft.map((x, i) => (i === idx ? { ...x, from: e.target.value } : x)))} className="h-9" data-testid={`prefix-from-${idx}`} />
                <Input type="number" min={0} value={r.to} onChange={(e) => setDraft(draft.map((x, i) => (i === idx ? { ...x, to: e.target.value } : x)))} className="h-9" data-testid={`prefix-to-${idx}`} />
                <Input value={r.prefix} maxLength={20} placeholder="RK-" onChange={(e) => setDraft(draft.map((x, i) => (i === idx ? { ...x, prefix: e.target.value } : x)))} className="h-9" data-testid={`prefix-text-${idx}`} />
                <Button variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => setDraft(draft.filter((_, i) => i !== idx))} data-testid={`prefix-del-${idx}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDraft([...draft, { from: "", to: "", prefix: "", field: "" }])} data-testid="prefix-add-btn"><Plus className="h-4 w-4" /> Rule add karo</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRulesOpen(false)} data-testid="prefix-cancel-btn">Cancel</Button>
            <Button onClick={saveRules} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="prefix-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
        <DialogContent className="max-w-sm" data-testid="pdf-range-dialog">
          <DialogHeader><DialogTitle>Kaunse Serial se Kaunse tak?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Sirf is serial range ke passes ki 12x18 PDF banegi.</p>
          <div className="flex items-end gap-3">
            <div className="flex-1"><Label className="text-xs">From Serial</Label><Input type="number" min={1} value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="h-10 mt-1" data-testid="pdf-range-from" /></div>
            <div className="flex-1"><Label className="text-xs">To Serial</Label><Input type="number" min={1} value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="h-10 mt-1" data-testid="pdf-range-to" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRangeOpen(false)} data-testid="pdf-range-cancel-btn">Cancel</Button>
            <Button onClick={confirmPdfRange} className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5" data-testid="pdf-range-confirm-btn"><FileDown className="h-4 w-4" /> PDF banao</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoCropDialog open={!!adjust} src={adjust?.src} onClose={() => setAdjust(null)} onDone={onAdjustDone} />
      <RandomTagDialog open={rtagOpen} onClose={() => setRtagOpen(false)} eventId={event.id} approvedCount={subs.filter((s) => s.status === "approved").length} onDone={load} />
      <Dialog open={!!tagFor} onOpenChange={(v) => !v && setTagFor(null)}>
        <DialogContent className="max-w-sm" data-testid="tag-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Tag className="h-4 w-4 text-amber-600" /> Pass Tag</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Is candidate ke pass par ye tag dikhega (top-right corner). Khaali chhodne par tag hat jayega.</p>
          <Input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="e.g. VIP, Guest, Paid" maxLength={30} data-testid="tag-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagFor(null)} data-testid="tag-cancel-btn">Cancel</Button>
            <Button onClick={saveTag} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="tag-save-btn">Save Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
